<template>
  <div class="cf-key-value">
    <!-- Subtotal -->
    <dl class="cf-key-value__row">
      <dt class="cf-key-value__key">
        Subtotal
      </dt>

      <dd class="cf-key-value__value">
        {{ $dollars(subtotal) }}
      </dd>
    </dl>

    <!-- Order Fees -->
    <template v-if="order.orderFees.length > 0">
      <dl
        v-for="({ name, amount, description }, idx) in order.orderFees"
        :key="idx"
        class="cf-key-value__row"
      >
        <dt class="cf-key-value__key">
          {{ name }}

          <button
            v-if="description"
            type="button"
            class="cursor-pointer"
            @click="$openModal('notice', { title: name, description, useSecondaryStyle: true, excludeButton: true })"
          >
            <span class="cf-icon fal fa-info-circle" />
          </button>
        </dt>

        <dd class="cf-key-value__value">
          {{ $dollars(amount) }}
        </dd>
      </dl>
    </template>

    <!-- Discount -->
    <dl
      v-if="order.discount"
      class="cf-key-value__row"
    >
      <dt class="cf-key-value__key">
        Discount
      </dt>
      <dd class="cf-key-value__value primary">
        -{{ $dollars(order.discount) }}
      </dd>
    </dl>

    <!-- Offers -->
    <div
      v-if="order.discount && order.offers.length > 0"
      class="cf-key-value__nested-section"
    >
      <div
        v-for="offer in order.offers"
        :key="offer.id"
        class="cf-key-value__row cf-key-value__row--justify-start"
      >
        <p class="cf-key-value__key">
          {{ offer.name || offer.promotionCode }}
        </p>

        <div
          v-if="order.orderAttributes && order.orderAttributes.canRemoveOffers && order.removingOfferIds"
          class="cf-key-value__value cf-padding-left-8"
          @click="removeOffer(offer)"
        >
          <button
            type="button"
            title="Remove this discount"
            tabindex="-1"
            class="cf-link cf-link--red"
            :class="{
              'cf-link--loading': order.removingOfferIds.includes(offer.id)
            }"
          >
            Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Delivery Charge -->
    <template v-if="order.delivery || order.deliveryCharges">
      <dl class="cf-key-value__row">
        <dt class="cf-key-value__key">
          Delivery Charge
        </dt>

        <dd class="cf-key-value__value">
          {{ $dollars(order.delivery ? order.delivery.fee : order.deliveryCharges) }}
        </dd>
      </dl>
    </template>

    <!-- Delivery Taxes and Fees Button Toggler -->
    <template v-if="order.delivery">
      <dl class="cf-key-value__row">
        <dt
          class="cf-key-value__key"
          @click="toggleDeliveryTaxesAndFeesOpen"
        >
          <button
            type="button"
            class="cf-link cf-link--has-icon"
          >
            Tax & Fee
            <span
              class="cf-icon far"
              :class="{
                'fa-angle-right': !deliveryTaxesAndFeesOpen,
                'fa-angle-down': deliveryTaxesAndFeesOpen
              }"
            />
          </button>
        </dt>

        <dd class="cf-key-value__value">
          {{ $dollars(order.tax + order.serviceCharges) }}
        </dd>
      </dl>

      <!-- Delivery Taxes and Fees -->
      <div
        v-if="deliveryTaxesAndFeesOpen"
        class="cf-key-value__nested-section"
      >
        <dl class="cf-key-value__row">
          <dt class="cf-key-value__key">
            Tax
          </dt>

          <dd class="cf-key-value__value">
            {{ $dollars(order.tax) }}
          </dd>
        </dl>

        <template v-if="order.serviceCharges">
          <dl class="cf-key-value__row">
            <dt class="cf-key-value__key">
              Service Fee
            </dt>

            <dd class="cf-key-value__value">
              {{ $dollars(order.serviceCharges) }}
            </dd>
          </dl>

          <div class="cf-key-value__row">
            <p class="smallest">
              <i>The service fee helps operate the delivery provider.</i>
            </p>
          </div>
        </template>
      </div>
    </template>

    <template v-else>
      <!-- Service Charges -->
      <dl
        v-if="order.serviceCharges"
        class="cf-key-value__row"
      >
        <dt class="cf-key-value__key">
          Service Charges
        </dt>

        <dd class="cf-key-value__value">
          {{ $dollars(order.serviceCharges) }}
        </dd>
      </dl>

      <!-- Tax -->
      <dl
        v-if="order.tax"
        class="cf-key-value__row"
      >
        <dt class="cf-key-value__key">
          Tax
        </dt>

        <dd class="cf-key-value__value">
          {{ $dollars(order.tax) }}
        </dd>
      </dl>
    </template>

    <!-- Tip -->
    <dl
      v-if="totals.tip || order.tip"
      class="cf-key-value__row"
    >
      <dt class="cf-key-value__key">
        Tip
      </dt>

      <dd class="cf-key-value__value">
        {{ $dollars(totals.tip || order.tip) }}
      </dd>
    </dl>

    <!-- Credit Card Surcharge -->
    <dl
      v-if="surchargeAmount"
      class="cf-key-value__row"
    >
      <dt class="cf-key-value__key">
        Credit Card Surcharge
      </dt>

      <dd class="cf-key-value__value">
        {{ $dollars(surchargeAmount) }}
      </dd>
    </dl>

    <!-- Shipping -->
    <dl
      v-if="order.shipment"
      class="cf-key-value__row"
    >
      <dt class="cf-key-value__key">
        Shipping
      </dt>

      <dd class="cf-key-value__value">
        {{ $dollars(order.shipment.fee) }}
      </dd>
    </dl>

    <!-- Total -->
    <dl class="cf-key-value__row cf-key-value__row--large">
      <dt class="cf-key-value__key">
        Total
      </dt>

      <dd class="cf-key-value__value">
        {{ $dollars(totals.totalWithTip) }}
      </dd>
    </dl>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';


  export default {
    name: 'CfOrderTotals',

    props: {
      order: {
        type: Object,
        required: true
      },

      totals: {
        type: Object,
        default() {
          return {
            tip: 0,
            totalWithTip: 0
          };
        }
      }
    },

    data() {
      return {
        deliveryTaxesAndFeesOpen: false
      };
    },

    computed: {
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('content', ['content']),

      subtotal() {
        return this.order.fulfillmentType === 'External'
          ? this.order.subtotal
          : this.order.orderItemTotal;
      },

      surchargeAmount() {
        return this.order.payments?.find(payment => payment.surcharge)?.surcharge;
      }
    },

    methods: {
      async removeOffer(offer) {
        await this.$store.dispatch('orders/removeOffer', {
          id: offer.id
        });

        // this.$triggerGoogleTrackingEvent('RemoveOffer', 'Loyalty');

        this.$triggerNewRelicEvent('RemoveOffer', 'Information', 'Removed Loyalty Offer');
      },

      toggleDeliveryTaxesAndFeesOpen() {
        this.deliveryTaxesAndFeesOpen = !this.deliveryTaxesAndFeesOpen;
      }
    }
  };
</script>
